import { useRef, useCallback } from 'react'
import { router } from '@inertiajs/react'

export interface SaveSubscriber {
  onSuccess?: () => void
  /** Called for a validation failure AND for an interrupted request. */
  onError?: (message: string) => void
  /** Always called once the request is done (success, failure, or cancel). */
  onSettled?: () => void
}

export interface QueuedSave extends SaveSubscriber {
  /**
   * Dedupe key — normally `${metricId}:${columnKey}`. A save enqueued for a
   * key that is still WAITING is merged into it rather than queued twice, so
   * a value edit followed immediately by a Won/Lost toggle on the same cell
   * becomes one request carrying both fields.
   */
  key: string
  url: string
  /** The full request body. */
  body: Record<string, unknown>
  /**
   * HTTP verb for the visit. Entry autosaves POST; the inline Target edit
   * writes through the metric UPDATE endpoint the settings modal uses, so it
   * has to PUT — and it has to do so through this queue, or its visit would
   * cancel a weekly autosave that is already in flight.
   */
  method?: 'post' | 'put'
  /**
   * Nested body key to shallow-merge when folding a duplicate into a
   * waiting item (e.g. `metric_entry`). Without it the newer body simply
   * replaces the older one.
   */
  mergeKey?: string
  /**
   * Props to reload from the response. Defaults to the tiny `autosave_ack`
   * ack prop, which is all a cell save needs. A write whose result must show
   * up elsewhere on the page overrides it — creating a topic from a metric
   * row asks for `topics` + `flash` so The List refreshes.
   */
  only?: string[]
}

interface PendingItem {
  key: string
  url: string
  body: Record<string, unknown>
  method?: 'post' | 'put'
  mergeKey?: string
  only?: string[]
  /**
   * Every operation folded into this request. Coalescing merges payloads,
   * but each contributing operation keeps its own lifecycle callbacks —
   * dropping them would strand its UI (a spinner that never clears, a modal
   * that never advances).
   */
  subscribers: SaveSubscriber[]
}

/**
 * Serializes Inertia saves so only one visit is ever in flight.
 *
 * Inertia's router cancels the active visit whenever a new one starts
 * (`activeVisit && cancelVisit(...)` in @inertiajs/core). Firing a request
 * per cell edit therefore means a fast Enter-through-a-column silently
 * aborts earlier saves — the value looks saved on screen but never reaches
 * the server. Queueing behind a single in-flight visit removes that class of
 * loss entirely; a cancel that still happens (e.g. the user navigates away)
 * is reported through `onError` instead of passing for success.
 */
export function useSaveQueue() {
  const queue = useRef<PendingItem[]>([])
  const inFlight = useRef(false)
  // Set once the owning page unmounts: a queued save must never launch an
  // Inertia visit after that, because its redirect back to this page would
  // cancel (or undo) the navigation the user just made.
  const disposed = useRef(false)

  // Empties the queue, telling each waiting operation it did not complete so
  // no spinner or modal is left hanging.
  const drain = useCallback((reason: string) => {
    queue.current.splice(0).forEach(item => item.subscribers.forEach(s => {
      s.onError?.(reason)
      s.onSettled?.()
    }))
  }, [])

  const pump = useCallback(() => {
    if (disposed.current || inFlight.current) return
    const item = queue.current.shift()
    if (!item) return
    inFlight.current = true

    // Inertia fires exactly one of onSuccess / onError / onCancel, then
    // onFinish. Track which so an interrupted request is never mistaken for
    // a successful save.
    let resolved = false
    let cancelled = false
    const notify = (fn: (s: SaveSubscriber) => void) => item.subscribers.forEach(fn)

    // Cast: Inertia types the body as RequestPayload, which a plain
    // Record<string, unknown> is structurally compatible with.
    const body = item.body as Parameters<typeof router.post>[1]
    const options = {
      preserveScroll: true,
      preserveState: true,
      // A real partial reload: `only: []` sends no partial headers at all
      // and the client swaps in a full set of props mid-typing. Asking for
      // the tiny `autosave_ack` prop keeps the response minimal. Inertia
      // always includes `errors` regardless of the partial list.
      only: item.only ?? ['autosave_ack'],
      onSuccess: () => {
        resolved = true
        notify(s => s.onSuccess?.())
      },
      onError: (errors: Record<string, string>) => {
        resolved = true
        const detail = Object.values(errors || {}).join(', ')
        notify(s => s.onError?.(detail))
      },
      onCancel: () => {
        resolved = true
        cancelled = true
        notify(s => s.onError?.('the request was interrupted'))
      },
      onFinish: () => {
        if (!resolved) {
          cancelled = true
          notify(s => s.onError?.('the request was interrupted'))
        }
        notify(s => s.onSettled?.())
        inFlight.current = false
        // A cancelled visit almost always means a navigation started. Pumping
        // the next save would cancel that navigation in turn, so stop and
        // report the rest instead of fighting the router. A later user edit
        // enqueues again and restarts the queue.
        if (cancelled) drain('the request was interrupted')
        else pump()
      },
    }

    // Called ON the router, never through a detached reference: Inertia's
    // router methods delegate to `this.visit`, so `const send = router.put`
    // throws before a request is ever made.
    if (item.method === 'put') router.put(item.url, body, options)
    else router.post(item.url, body, options)
  }, [])

  const enqueue = useCallback((item: QueuedSave) => {
    const { onSuccess, onError, onSettled, ...rest } = item
    const subscriber: SaveSubscriber = { onSuccess, onError, onSettled }
    const waiting = queue.current.find(q => q.key === item.key)
    if (waiting) {
      // Same cell, still waiting its turn: fold the new fields into that
      // request and ADD this operation's callbacks — the payload is
      // superseded, but both operations still need to be told what happened.
      const mk = item.mergeKey
      if (mk && waiting.body[mk] && item.body[mk]) {
        waiting.body = {
          ...waiting.body,
          ...item.body,
          [mk]: {
            ...(waiting.body[mk] as Record<string, unknown>),
            ...(item.body[mk] as Record<string, unknown>),
          },
        }
      } else {
        waiting.body = { ...waiting.body, ...item.body }
      }
      waiting.url = item.url
      waiting.mergeKey = item.mergeKey ?? waiting.mergeKey
      waiting.only = item.only ?? waiting.only
      waiting.subscribers.push(subscriber)
      return
    }
    queue.current.push({ ...rest, subscribers: [subscriber] })
    pump()
  }, [pump])

  /** Call on unmount: no further visits, and nothing left waiting silently. */
  const dispose = useCallback(() => {
    disposed.current = true
    drain('the page closed before the save completed')
  }, [drain])

  return { enqueue, dispose }
}
